import type { AdminCertification } from '@/api/(admin)/certifications/types';
import type { UpdateCertificationPayload } from '@/api/certifications/types';

const toIso = (v: string | null | undefined) =>
  v ? new Date(v).toISOString() : null;

const strOrNull = (v: string | null | undefined) => v || null;

/**
 * Builds the body for `PUT /api/admin/claims/:id`.
 *
 * Same reasoning as the customer builder: the handler writes every column it
 * is given and the detail page saves one field at a time, so a partial body
 * silently nulls whatever it omits — every field is echoed back from the
 * record unless the patch names it. `'x' in patch` rather than a truthiness
 * check, so an explicit `null` still clears a value.
 *
 * Unlike `buildClaimUpdatePayload` it echoes the BADGE / PAYMENT fields (title,
 * featured image, wages) an operator can edit, and never sends
 * `shouldAttachChildren`. Neither endpoint accepts `standardId` — the standard
 * is fixed once the certificate exists.
 */
export function buildAdminClaimUpdatePayload(
  cert: AdminCertification,
  patch: UpdateCertificationPayload,
): Record<string, unknown> {
  const editingProducts = 'styleIds' in patch;

  return {
    expirationDate: toIso(
      'expirationDate' in patch ? patch.expirationDate : cert.expirationDate,
    ),
    issueDate: toIso('issueDate' in patch ? patch.issueDate : cert.issueDate),
    documentNumber: strOrNull(
      'documentNumber' in patch ? patch.documentNumber : cert.documentNumber,
    ),
    licenseNumber: strOrNull(
      'licenseNumber' in patch ? patch.licenseNumber : cert.licenseNumber,
    ),
    issuedBy: strOrNull('issuedBy' in patch ? patch.issuedBy : cert.issuedBy),
    explanation: strOrNull(
      'explanation' in patch ? patch.explanation : cert.explanation,
    ),
    fileId: ('fileId' in patch ? patch.fileId : cert.fileId) ?? null,
    title: strOrNull('title' in patch ? patch.title : cert.title),
    featuredImageFileId:
      ('featuredImageFileId' in patch
        ? patch.featuredImageFileId
        : cert.featuredImageFileId) ?? null,
    minimumWage: strOrNull(
      'minimumWage' in patch ? patch.minimumWage : cert.minimumWage,
    ),
    livingWage: strOrNull(
      'livingWage' in patch ? patch.livingWage : cert.livingWage,
    ),
    // Stored as `certificateDependencies.products`; the endpoint keeps only the
    // ids that are still styles.
    styleIds: editingProducts
      ? (patch.styleIds ?? [])
      : (cert.certificateDependencies?.products ?? []),
  };
}
